/**
 * New-UX "Impersonate" dropdown in header, replacing "View X's task page"
 */
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  EmployeeAppRerouterHints,
  IExperience,
  PARTICIPANT_ACTOR_KEY,
  SupportingActorType
} from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import { ChevronDown, Eye } from 'lucide-react';
import React, { useMemo, useRef, useState } from 'react';
import { ClickAwayListener, Grow, Paper, Popper } from '@material-ui/core';
import { useUnsafeTranslation } from '../../../../../i18next';
import { toStandaloneAppUrl } from '../../../../../services/experiences/instance';
import { ActorDescriptor } from '../../../components/Actors';
import { NewUxButton } from '../../../components/Builders/newux/kit/Button';
import { tokens } from '../../../components/Builders/newux/kit/tokens';

const { color, font, radius } = tokens;

const Menu = styled(Paper)`
  min-width: 200px;
  border-radius: ${radius.md}px;
  box-shadow: ${tokens.shadow.md};
  overflow: hidden;
`;

const MenuItemLink = styled('a')`
  display: block;
  padding: ${tokens.space(2.5)} ${tokens.space(3)};
  font-size: ${font.variant.body2.fontSize}px;
  font-weight: ${font.weightScale.regular};
  color: ${color.textPrimary};
  cursor: pointer;
  &:hover {
    background: ${color.white};
  }
`;

export const ImpersonateMenu: React.FC<{
  d: Doc<IExperience>;
  actorDescriptors: { [key: string]: ActorDescriptor };
}> = ({ d, actorDescriptors }) => {
  const { t } = useUnsafeTranslation();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const actors = useMemo(
    () =>
      Object.entries(actorDescriptors)
        .filter(([, desc]) => desc.type !== SupportingActorType.CHANNEL)
        .map(([key, desc]) => ({ key, label: desc.label })),
    [actorDescriptors]
  );

  if (!actors.length) return null;

  return (
    <>
      <NewUxButton
        ref={anchorRef}
        variant="outlined"
        color="neutral"
        startIcon={<Eye size={16} />}
        onClick={() => setOpen((x) => !x)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {t('Impersonate')} <ChevronDown size={16} />
      </NewUxButton>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        style={{ zIndex: 2 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom'
            }}
          >
            <Menu>
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <div role="menu">
                  {actors.map((option) => (
                    <MenuItemLink
                      key={option.key}
                      target="_blank"
                      rel="noopener"
                      onClick={() => setOpen(false)}
                      href={toStandaloneAppUrl(
                        d,
                        option.key,
                        option.key === PARTICIPANT_ACTOR_KEY
                          ? undefined
                          : EmployeeAppRerouterHints.FILTER_ALL
                      )}
                    >
                      {option.label}
                    </MenuItemLink>
                  ))}
                </div>
              </ClickAwayListener>
            </Menu>
          </Grow>
        )}
      </Popper>
    </>
  );
};
